"use client";

import {
  Suspense,
  startTransition,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { ArrowRight, Camera, Store, Wrench } from "lucide-react";

import { Button } from "@/components/ui/button";
import { CustomWheelUpload } from "@/components/landing/custom-wheel-upload";
import { FAQSection } from "@/components/landing/faq-section";
import { HeroSection1 } from "@/components/landing/hero-section-1";
import { HowItWorksSection } from "@/components/landing/how-it-works-section";
import { InteractiveShowcase } from "@/components/landing/interactive-showcase";
import { LandingPricingSection } from "@/components/landing/landing-pricing-section";
import { HomeGalleryCta } from "@/components/gallery/home-gallery-cta";
import { useCredits } from "@/hooks/use-credits";
import { signIn, useSession } from "@/lib/auth/client";
import {
  canUseFreePreviewForUser,
  formatFreePreviewTrustLine,
  freePreviewEligibilityFromCredits,
} from "@/lib/payments/free-preview-config";

const SHOWCASE_SCENARIOS = [
  {
    name: "Ford Mustang GT",
    before: "/showcase/images/before5.webp",
    imageClassName: "object-[center_bottom]",
    wheels: [
      {
        id: "mustang-silver",
        name: "Silver multi-spoke",
        image: "/showcase/images/after51.webp",
        thumbnail: "/showcase/images/after51.webp",
      },
      {
        id: "mustang-bronze",
        name: "Bronze ten-spoke",
        image: "/showcase/images/after52.webp",
        thumbnail: "/showcase/images/after52.webp",
      },
      {
        id: "mustang-black",
        name: "Black multi-spoke",
        image: "/showcase/images/after53.webp",
        thumbnail: "/showcase/images/after53.webp",
      },
    ],
  },
  {
    name: "Ford F-150",
    before: "/showcase/images/before6.webp",
    imageClassName: "object-[center_bottom]",
    beforeImageClassName: "bottom-[20px]",
    wheels: [
      {
        id: "f150-option-1",
        name: "Machined multi-spoke",
        image: "/showcase/images/after61.webp",
        thumbnail: "/showcase/images/after61.webp",
      },
      {
        id: "f150-option-2",
        name: "Black off-road",
        image: "/showcase/images/after62.webp",
        thumbnail: "/showcase/images/after62.webp",
      },
      {
        id: "f150-option-3",
        name: "Dark multi-spoke",
        image: "/showcase/images/after63.webp",
        thumbnail: "/showcase/images/after63.webp",
      },
    ],
  },
  {
    name: "Red sport sedan",
    before: "/showcase/images/before1.jpg",
    imageClassName: "object-[center_70%]",
    wheels: [
      {
        id: "sedan-bronze",
        name: "Bronze ten-spoke",
        image: "/showcase/images/after11.webp",
        thumbnail: "/showcase/images/after11.webp",
      },
      {
        id: "sedan-silver",
        name: "Silver multi-spoke",
        image: "/showcase/images/after12.webp",
        thumbnail: "/showcase/images/after12.webp",
      },
      {
        id: "sedan-dark",
        name: "Silver five-spoke",
        image: "/showcase/images/after13.webp",
        thumbnail: "/showcase/images/after13.webp",
      },
    ],
  },
  {
    name: "White BMW sedan",
    before: "/showcase/images/before4.jpg",
    wheels: [
      {
        id: "bmw-bronze",
        name: "Bronze five-spoke",
        image: "/showcase/images/after41.webp",
        thumbnail: "/showcase/images/after41.webp",
      },
      {
        id: "bmw-black",
        name: "Black multi-spoke",
        image: "/showcase/images/after42.webp",
        thumbnail: "/showcase/images/after42.webp",
      },
    ],
  },
] as const;

const FAQS = [
  {
    question: "How does the wheel visualizer work?",
    answer:
      "Upload a clear photo of your car, select a wheel from the free catalog, and generate a realistic visual preview with matched perspective and lighting. Every paid pack also unlocks uploads of your own wheel images.",
  },
  {
    question: "What kind of car and wheel photos work best?",
    answer:
      "Clear, well-lit car photos with the wheels visible work best. For custom wheels, use a clean product photo, listing image, or phone photo where the wheel design is easy to see.",
  },
  {
    question: "Is this a physical fitment check?",
    answer:
      "No. Wheel Visualizer helps you compare how different styles look on your car. Always confirm diameter, offset, bolt pattern, tire clearance, and other fitment details with the wheel seller or installer.",
  },
  {
    question: "Do I need a subscription?",
    answer:
      "No. Buy a credit pack when you need more previews. Your credits never expire and there are no recurring charges.",
  },
  {
    question: "How are my uploaded photos used?",
    answer:
      "We use your uploads to create and store your previews. Original uploads are not published, but eligible free-preview results may be selected for our public gallery and product marketing under our Terms. You can request removal at any time.",
  },
] as const;

const HOW_IT_WORKS = [
  {
    step: "01",
    title: "Upload",
    description: "Add a clear photo of your car with the wheels visible.",
    image: "/steps/car-sample.webp",
    alt: "Car photo ready to upload",
  },
  {
    step: "02",
    title: "Choose",
    description: "Pick a free catalog wheel. Paid packs also unlock your own wheel uploads.",
    image: "/steps/wheel-sample.webp",
    alt: "Wheel selection in the visualizer",
  },
  {
    step: "03",
    title: "Preview",
    description: "Generate a realistic visual comparison on your actual car.",
    image: "/steps/final-sample.webp",
    alt: "Completed wheel style preview",
  },
] as const;

export default function HomePage() {
  const heroCtaRef = useRef<HTMLDivElement>(null);
  const [showStickyCta, setShowStickyCta] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const { data: session } = useSession();
  const { data: credits } = useCredits();
  const router = useRouter();

  useEffect(() => {
    startTransition(() => setIsMounted(true));
  }, []);

  const isAuthenticated = isMounted ? !!session?.user : false;
  const canUseFreePreview =
    isAuthenticated &&
    canUseFreePreviewForUser(freePreviewEligibilityFromCredits(credits));
  const ctaText =
    isAuthenticated && !canUseFreePreview ? "Go to App" : "Start for free";
  const freePreviewTrustLine = formatFreePreviewTrustLine();

  useEffect(() => {
    const target = heroCtaRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      ([entry]) => setShowStickyCta(!entry.isIntersecting),
      { threshold: 0.1 }
    );
    observer.observe(target);
    return () => observer.unobserve(target);
  }, []);

  const handleTryItNow = useCallback(() => {
    if (session?.user) {
      router.push("/app");
      return;
    }

    signIn.social({
      provider: "google",
      callbackURL: "/app",
    });
  }, [router, session?.user]);

  const schemas = [
    {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "The Wheel Visualizer",
      url: "https://thewheelvisualizer.com",
      description:
        "Upload a car photo and create realistic visual previews with different wheel styles.",
      applicationCategory: "MultimediaApplication",
      operatingSystem: "Web",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    },
    {
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: "How to preview wheel styles on your car",
      step: HOW_IT_WORKS.map((item) => ({
        "@type": "HowToStep",
        name: item.title,
        text: item.description,
        image: `https://thewheelvisualizer.com${item.image}`,
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "The Wheel Visualizer",
      url: "https://thewheelvisualizer.com",
      logo: "https://thewheelvisualizer.com/logo-rim.png",
    },
  ];

  return (
    <div className="flex flex-col">
      {schemas.map((schema) => (
        <script
          key={schema["@type"]}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}

      <HeroSection1
        onTryItNow={handleTryItNow}
        ctaText={ctaText}
        ctaRef={heroCtaRef}
      />

      <HowItWorksSection
        steps={HOW_IT_WORKS}
        onTryItNow={handleTryItNow}
      />

      <section
        id="showcase"
        className="relative overflow-hidden bg-[#080A0D] px-6 py-24 md:py-32"
      >
        <div className="pointer-events-none absolute right-0 top-1/4 h-96 w-96 rounded-full bg-white/[0.025] blur-3xl" />
        <div className="relative mx-auto max-w-7xl">
          <div className="grid items-end gap-6 md:grid-cols-[1fr_auto]">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-white/35">
                Explore the possibilities
              </p>
              <h2 className="mt-4 max-w-2xl text-4xl font-bold tracking-tight text-white md:text-6xl">
                One car. A completely different character.
              </h2>
            </div>
            <p className="max-w-sm text-base leading-relaxed text-white/50 md:pb-2 md:text-right">
              Switch between wheel options, then drag the comparison to inspect
              every detail.
            </p>
          </div>

          <div className="mt-12 space-y-5 rounded-[2rem] border border-white/10 bg-gradient-to-b from-white/[0.05] to-white/[0.015] p-3 shadow-2xl shadow-black/20 md:p-5">
            {SHOWCASE_SCENARIOS.map((scenario, index) => (
              <article
                key={scenario.name}
                className="overflow-hidden rounded-[1.4rem] border border-white/[0.07] bg-[#0D1014] p-3 md:p-5"
              >
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3 px-1">
                  <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/[0.06] text-xs font-semibold text-white/50">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/30">
                        Live comparison
                      </p>
                      <h3 className="mt-0.5 text-lg font-semibold text-white">
                        {scenario.name}
                      </h3>
                    </div>
                  </div>
                  <Button
                    onClick={handleTryItNow}
                    variant="outline"
                    className="rounded-xl border-white/15 bg-white/[0.03] text-white hover:bg-white/[0.08]"
                  >
                    Try on my car
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
                <InteractiveShowcase
                  scenarioName={scenario.name}
                  beforeImage={scenario.before}
                  wheels={[...scenario.wheels]}
                  imageClassName={"imageClassName" in scenario ? scenario.imageClassName : undefined}
                  beforeImageClassName={"beforeImageClassName" in scenario ? scenario.beforeImageClassName : undefined}
                  priority={index === 0}
                />
              </article>
            ))}
          </div>

          <div className="mt-8 flex flex-col items-center justify-between gap-5 rounded-2xl border border-white/[0.07] bg-white/[0.025] px-6 py-5 sm:flex-row">
            <p className="text-center text-sm text-white/45 sm:text-left">
              Your car and your wheel choice are what make the preview useful.
            </p>
            <Button
              onClick={handleTryItNow}
              size="lg"
              className="w-full bg-white text-black hover:bg-white/90 sm:w-auto"
            >
              Create my comparison
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      <HomeGalleryCta />

      <section className="bg-[#0A0C0F] px-6 py-24 md:py-32">
        <CustomWheelUpload onTryItNow={handleTryItNow} />
      </section>

      <Suspense
        fallback={
          <section className="bg-[#0A0C0F] px-6 py-24">
            <div className="mx-auto grid max-w-7xl gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
              {Array.from({ length: 5 }).map((_, index) => (
                <div
                  key={index}
                  className="h-72 animate-pulse rounded-2xl border border-white/10 bg-white/[0.03]"
                />
              ))}
            </div>
          </section>
        }
      >
        <LandingPricingSection />
      </Suspense>

      <section className="relative overflow-hidden bg-[#080A0D] px-6 py-24 md:py-32">
        <div className="relative mx-auto max-w-7xl">
          <div className="grid items-end gap-6 md:grid-cols-[1fr_auto]">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-white/35">
                Made to be useful
              </p>
              <h2 className="mt-4 max-w-2xl text-4xl font-bold tracking-tight text-white md:text-6xl">
                Built for real wheel decisions
              </h2>
            </div>
            <p className="max-w-sm text-base leading-relaxed text-white/50 md:pb-2 md:text-right">
              A clearer way to discuss, compare, and confidently choose a
              direction.
            </p>
          </div>

          <div className="mt-12 rounded-[2rem] border border-white/10 bg-gradient-to-b from-white/[0.055] to-white/[0.02] p-3 md:p-5">
            <div className="grid gap-3 md:grid-cols-3">
            {[
              {
                title: "Enthusiasts & Builders",
                copy: "Compare styles on your own build before ordering the real set.",
                icon: Wrench,
                label: "For your own build",
              },
              {
                title: "Wheel Shops & Dealers",
                copy: "Help customers visualize options on the car they actually drive.",
                icon: Store,
                label: "For customer confidence",
              },
              {
                title: "Detailers & Studios",
                copy: "Present wheel ideas with polished visuals that are easy to discuss.",
                icon: Camera,
                label: "For better presentations",
              },
            ].map((audience, index) => {
              const Icon = audience.icon;
              return (
                <article
                  key={audience.title}
                  className="group relative min-h-72 overflow-hidden rounded-[1.4rem] border border-white/[0.07] bg-[#0D1014] p-7"
                >
                  <span className="absolute right-5 top-4 text-6xl font-bold tracking-tighter text-white/[0.025]">
                    0{index + 1}
                  </span>
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.06] transition-transform duration-300 group-hover:-translate-y-1">
                    <Icon className="h-5 w-5 text-white" aria-hidden />
                  </div>
                  <p className="mt-10 text-[10px] font-semibold uppercase tracking-[0.17em] text-white/35">
                    {audience.label}
                  </p>
                  <h3 className="mt-3 text-2xl font-semibold text-white">
                    {audience.title}
                  </h3>
                  <p className="mt-3 leading-relaxed text-white/55">
                    {audience.copy}
                  </p>
                </article>
              );
            })}
            </div>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-[#0A0C0F] px-6 py-24 md:py-32">
        <div className="pointer-events-none absolute left-1/2 top-0 h-80 w-[60rem] -translate-x-1/2 rounded-full bg-white/[0.025] blur-3xl" />
        <div className="relative mx-auto max-w-7xl">
          <div className="grid gap-12 lg:grid-cols-[0.7fr_1.3fr] lg:gap-16">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-white/35">
                Good to know
              </p>
              <h2 className="mt-4 text-4xl font-bold tracking-tight text-white md:text-6xl">
                Questions before your first preview?
              </h2>
              <p className="mt-5 max-w-md leading-relaxed text-white/50">
                The essentials about image quality, fitment, credits, and how
                your uploads are handled.
              </p>
              <div className="mt-8 hidden rounded-2xl border border-white/[0.07] bg-white/[0.025] p-5 text-sm text-white/45 lg:block">
                Still deciding? {freePreviewTrustLine} No credit card required.
              </div>
            </div>
            <div className="rounded-[2rem] border border-white/10 bg-gradient-to-b from-white/[0.05] to-white/[0.015] p-3 md:p-5">
              <FAQSection faqs={[...FAQS]} />
            </div>
          </div>

          <div className="relative mt-16 overflow-hidden rounded-[2rem] border border-white/10 bg-white/[0.045] px-6 py-10 md:px-10 md:py-12">
            <div className="pointer-events-none absolute -right-20 -top-32 h-80 w-80 rounded-full bg-white/[0.055] blur-3xl" />
            <div className="relative flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-white/35">
                  Your next look starts here
                </p>
                <h2 className="mt-3 max-w-2xl text-3xl font-bold tracking-tight text-white md:text-4xl">
                  Ready to see your car with new wheels?
                </h2>
                <p className="mt-3 text-sm text-white/45">
                  {freePreviewTrustLine.replace(/\.$/, "")} - No credit card required
                </p>
              </div>
              <div className="flex w-full shrink-0 flex-col gap-3 sm:w-auto sm:flex-row">
              <Button
                size="lg"
                onClick={handleTryItNow}
                  className="h-12 bg-white text-black hover:bg-white/90"
              >
                {ctaText === "Start for free" ? "Create my free preview" : ctaText}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
                <Button asChild size="lg" variant="outline" className="h-12 border-white/15 bg-white/[0.02] text-white hover:bg-white/[0.07]">
                <Link href="#pricing">View pricing</Link>
              </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {showStickyCta ? (
        <div className="fixed inset-x-0 bottom-0 z-50 border-t border-white/10 bg-background/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-sm md:hidden">
          <div className="px-4 py-3">
            <Button
              size="lg"
              onClick={handleTryItNow}
              className="h-12 w-full bg-white text-base text-black hover:bg-white/95"
            >
              {ctaText === "Start for free" ? "Create my free preview" : ctaText}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
